import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { API, toISODate, shiftDate, formatShortDate, downloadBlob } from "@/lib/attendance";

const ESITO_STYLE = {
  presente: "bg-green-100 text-green-800",
  assente: "bg-slate-200 text-slate-700",
  rimandata: "bg-amber-100 text-amber-800",
  ospite: "bg-orange-100 text-orange-800",
};
const CELL_STYLE = { P: "bg-green-500 text-white", A: "bg-slate-200 text-slate-600", R: "bg-amber-200 text-amber-900", "-": "bg-slate-50 text-slate-400", "": "bg-white text-slate-300" };

function useRange() {
  const today = toISODate(new Date());
  const [from, setFrom] = useState(shiftDate(today, -30));
  const [to, setTo] = useState(today);
  return { from, setFrom, to, setTo };
}

function RangeInputs({ range }) {
  return (
    <>
      <div className="space-y-1">
        <Label className="text-xs">Dal</Label>
        <Input type="date" value={range.from} onChange={(e) => range.setFrom(e.target.value)} data-testid="report-date-from" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Al</Label>
        <Input type="date" value={range.to} onChange={(e) => range.setTo(e.target.value)} data-testid="report-date-to" />
      </div>
    </>
  );
}

function useReport(token, kind, idKey, id, range) {
  const [data, setData] = useState(null);
  const params = { [idKey]: id, date_from: range.from || undefined, date_to: range.to || undefined };
  useEffect(() => {
    if (!id) { setData(null); return; }
    axios.get(`${API}/admin/attendance/report/${kind}`, { headers: { Authorization: `Bearer ${token}` }, params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore nel report"));
    // eslint-disable-next-line
  }, [id, range.from, range.to, token]);
  const exportCsv = async (filename) => {
    try {
      const r = await axios.get(`${API}/admin/attendance/report/${kind}`, {
        headers: { Authorization: `Bearer ${token}` }, params: { ...params, format: "csv" }, responseType: "blob",
      });
      downloadBlob(r.data, filename);
    } catch {
      toast.error("Errore nell'esportazione");
    }
  };
  return { data, exportCsv };
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
      <div className="text-2xl font-extrabold text-slate-900" data-testid={testid}>{value ?? "—"}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

export function StudentReport({ token, registrations }) {
  const range = useRange();
  const [id, setId] = useState("");
  const sorted = useMemo(() => [...registrations].sort((a, b) =>
    `${a.figlio.cognome} ${a.figlio.nome}`.localeCompare(`${b.figlio.cognome} ${b.figlio.nome}`)), [registrations]);
  const { data, exportCsv } = useReport(token, "student", "registration_id", id, range);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Allievo</Label>
          <Select value={id} onValueChange={setId}>
            <SelectTrigger data-testid="report-student-select"><SelectValue placeholder="Scegli un allievo" /></SelectTrigger>
            <SelectContent className="max-h-80">
              {sorted.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.figlio.cognome} {r.figlio.nome} ({(r.figlio.data_nascita || "").slice(0, 4)})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <RangeInputs range={range} />
        <Button disabled={!data} onClick={() => exportCsv(`presenze_${data.student.cognome}_${data.student.nome}.csv`)}
          className="gap-2 bg-[#15803D] hover:bg-[#166534]" data-testid="report-student-export-btn">
          <Download className="w-4 h-4" /> CSV
        </Button>
      </div>
      {!data && <div className="text-sm text-slate-500 py-8 text-center">Scegli un allievo per vedere le sue presenze.</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Presenti" value={data.summary.presenti} testid="student-stat-present" />
            <Stat label="Assenti" value={data.summary.assenti} testid="student-stat-absent" />
            <Stat label="Rimandate" value={data.summary.rimandate} testid="student-stat-postponed" />
            <Stat label="Come ospite" value={data.summary.ospite} testid="student-stat-guest" />
            <Stat label="Tasso presenze" value={data.summary.tasso != null ? `${data.summary.tasso}%` : null} testid="student-stat-rate" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="student-report-table">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-2">Data</th><th className="px-4 py-2">Gruppo</th><th className="px-4 py-2">Orario</th><th className="px-4 py-2">Esito</th><th className="px-4 py-2">Maestro</th></tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-mono">{formatShortDate(r.date)}</td>
                    <td className="px-4 py-2">{r.group_nome}{r.guest && <span className="ml-1 text-xs text-orange-700">(ospite)</span>}</td>
                    <td className="px-4 py-2 font-mono">{r.start_time}–{r.end_time}</td>
                    <td className="px-4 py-2">
                      <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold capitalize", ESITO_STYLE[r.esito])}>{r.esito}</span>
                      {r.postpone_reason && <span className="ml-2 text-xs text-slate-500">{r.postpone_reason}</span>}
                    </td>
                    <td className="px-4 py-2 capitalize">{r.teacher}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Nessuna lezione registrata nel periodo.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export function GroupReport({ token, groups }) {
  const range = useRange();
  const [id, setId] = useState("");
  const { data, exportCsv } = useReport(token, "group", "group_id", id, range);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Gruppo</Label>
          <Select value={id} onValueChange={setId}>
            <SelectTrigger data-testid="report-group-select"><SelectValue placeholder="Scegli un gruppo" /></SelectTrigger>
            <SelectContent>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <RangeInputs range={range} />
        <Button disabled={!data} onClick={() => exportCsv(`presenze_gruppo_${data.group.nome}.csv`)}
          className="gap-2 bg-[#15803D] hover:bg-[#166534]" data-testid="report-group-export-btn">
          <Download className="w-4 h-4" /> CSV
        </Button>
      </div>
      {!data && <div className="text-sm text-slate-500 py-8 text-center">Scegli un gruppo per vedere il registro.</div>}
      {data && <GroupTable data={data} />}
    </div>
  );
}

function GroupTable({ data }) {
  const guests = data.lessons.flatMap((l) => l.guests.map((g) => ({ ...g, date: l.date })));
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Lezioni svolte" value={data.summary.lezioni_svolte} testid="group-stat-held" />
        <Stat label="Rimandate" value={data.summary.rimandate} testid="group-stat-postponed" />
        <Stat label="Ospiti" value={data.summary.ospiti} testid="group-stat-guests" />
        <Stat label="Tasso presenze" value={data.summary.tasso != null ? `${data.summary.tasso}%` : null} testid="group-stat-rate" />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="text-sm min-w-full" data-testid="group-report-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left sticky left-0 bg-slate-50">Allievo</th>
              {data.lessons.map((l) => (
                <th key={l.id} className="px-2 py-2 font-mono text-center whitespace-nowrap" title={l.postpone_reason}>
                  {formatShortDate(l.date).slice(0, 5)}<div className="text-[10px] font-normal">{l.start_time}</div>
                </th>
              ))}
              <th className="px-3 py-2">P</th><th className="px-3 py-2">A</th><th className="px-3 py-2">%</th>
            </tr>
          </thead>
          <tbody>
            {data.students.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="px-4 py-2 whitespace-nowrap sticky left-0 bg-white">{s.cognome} {s.nome} <span className="text-slate-400">({s.anno})</span></td>
                {s.cells.map((c, i) => (
                  <td key={i} className="px-1 py-1 text-center">
                    <span className={cn("inline-flex w-7 h-7 rounded-md items-center justify-center text-xs font-bold", CELL_STYLE[c])}>{c || "·"}</span>
                  </td>
                ))}
                <td className="px-3 py-2 text-center font-semibold">{s.presenti}</td>
                <td className="px-3 py-2 text-center">{s.assenti}</td>
                <td className="px-3 py-2 text-center">{s.tasso != null ? s.tasso : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.lessons.length && <div className="px-4 py-6 text-center text-slate-500 text-sm">Nessuna lezione registrata nel periodo.</div>}
      </div>
      <div className="text-xs text-slate-500">P = presente · A = assente · R = lezione rimandata · - = non previsto quel giorno</div>
      {guests.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="group-guests">
          <div className="text-sm font-bold text-slate-800 mb-2">Ospiti</div>
          <ul className="text-sm text-slate-700 space-y-1">
            {guests.map((g, i) => (
              <li key={i}>{formatShortDate(g.date)} — {g.nome} {g.cognome} {g.anno && `(${g.anno})`} · {g.registration_id ? "da altro gruppo" : "esterno"}{!g.present && " · assente"}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
